/**
 * Compare.jsx - Head-to-head player comparison page.
 *
 * Users pick two players from the current week's projections and see their
 * ML predictions, confidence intervals, and 2023-2024 historical averages
 * side by side. The comparison can be exported to CSV.
 *
 * Player IDs can be passed via router state (e.g. from the Dashboard
 * compare bar) to pre-populate the slots on load.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { BarChart3, Search, X, Download, RefreshCw, AlertCircle } from 'lucide-react';

// Season and week are always loaded dynamically from the API

// Position badge styles
const getPosStyle = (pos) => {
  switch (pos) {
    case 'QB': return 'bg-pink-500/10 text-pink-400 border-pink-500/20';
    case 'RB': return 'bg-green-500/10 text-green-400 border-green-500/20';
    case 'WR': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    case 'TE': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
    default:   return 'bg-white/10 text-white/70 border-white/20';
  }
};

const getInitials = (name) =>
  name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '??';

const formatCI = (val) => (val != null ? Number(val).toFixed(1) : '—');

// Risk label based on the width of the confidence interval
const getRisk = (ciLow, ciHigh) => {
  if (ciLow == null || ciHigh == null) return { label: '—', color: 'text-white/40' };
  const spread = ciHigh - ciLow;
  if (spread > 18) return { label: 'High Risk',   color: 'text-red-400' };
  if (spread > 10) return { label: 'Medium Risk', color: 'text-yellow-400' };
  return                  { label: 'Low Risk',    color: 'text-green-400' };
};

/**
 * SVG bar chart that renders one bar per player for a given stat key.
 * Used in the historical averages section.
 */
const CompareBarChart = ({ slots, statKey, label, color0 = '#4d7cff', color1 = '#a78bfa' }) => {
  const v0 = slots[0]?.history ? (slots[0].history.stats.reduce((s, r) => s + (r[statKey] || 0), 0) / Math.max(slots[0].history.stats.length, 1)) : null;
  const v1 = slots[1]?.history ? (slots[1].history.stats.reduce((s, r) => s + (r[statKey] || 0), 0) / Math.max(slots[1].history.stats.length, 1)) : null;
  if (v0 == null && v1 == null) return null;
  const max = Math.max(v0 || 0, v1 || 0, 1);
  const barH = 28;
  const w = 280;
  return (
    <div className="mb-3">
      <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-1.5">{label} (avg/game)</div>
      <svg width="100%" viewBox={`0 0 ${w} ${barH * 2 + 8}`} className="overflow-visible">
        {v0 != null && (
          <g>
            <rect x={0} y={0} width={(v0 / max) * w} height={barH - 2} rx={4} fill={color0} fillOpacity={0.7} />
            <text x={(v0 / max) * w + 6} y={barH - 10} fill="white" fontSize={11} fontWeight="bold">{v0.toFixed(1)}</text>
          </g>
        )}
        {v1 != null && (
          <g>
            <rect x={0} y={barH + 6} width={(v1 / max) * w} height={barH - 2} rx={4} fill={color1} fillOpacity={0.7} />
            <text x={(v1 / max) * w + 6} y={barH * 2 - 4} fill="white" fontSize={11} fontWeight="bold">{v1.toFixed(1)}</text>
          </g>
        )}
      </svg>
    </div>
  );
};

const Compare = () => {
  const location  = useLocation();
  const navigate  = useNavigate();

  const [season,        setSeason]        = useState(null);
  const [week,          setWeek]          = useState(null);
  const [allPlayers,    setAllPlayers]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [slots,         setSlots]         = useState([null, null]); // the two selected players
  const [searches,      setSearches]      = useState(['', '']);
  const [openSlot,      setOpenSlot]      = useState(null);        // which dropdown is open
  const [histories,     setHistories]     = useState([null, null]); // historical stats per slot
  const [histLoading,   setHistLoading]   = useState([false, false]);
  const [exportToast,   setExportToast]   = useState(false);

  // Determine the latest available week, then load projections for it
  useEffect(() => {
    api.getAvailableWeeks()
      .then(weeks => {
        if (!weeks || weeks.length === 0) { setSeason(2026); setWeek(1); return; }
        const sorted = [...weeks].sort((a, b) => b.season !== a.season ? b.season - a.season : b.week - a.week);
        setSeason(sorted[0].season);
        setWeek(sorted[0].week);
      })
      .catch(() => { setSeason(2026); setWeek(1); });
  }, []);

  useEffect(() => {
    if (!season || !week) return;
    api.getProjections({ season, week })
      .then(data => {
        const players = (data.players || []).map(p => ({
          id:       p.id,
          name:     p.name,
          pos:      p.pos,
          team:     p.team,
          proj:     p.proj,
          ciLow:    p.ciLow  != null ? p.ciLow  : null,
          ciHigh:   p.ciHigh != null ? p.ciHigh : null,
          imageUrl: p.imageUrl || '',
        }));
        setAllPlayers(players);

        // Pre-populate slots if player IDs were passed via router state
        const ids = location.state?.playerIds || [];
        if (ids.length > 0) {
          const pre = ids.slice(0, 2).map(id => players.find(p => p.id === id) || null);
          setSlots([pre[0] || null, pre[1] || null]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [season, week]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch historical stats whenever a slot's player changes
  const fetchHistory = useCallback(async (slotIndex, playerId) => {
    if (!playerId) {
      setHistories(prev => { const n = [...prev]; n[slotIndex] = null; return n; });
      return;
    }
    setHistLoading(prev => { const n = [...prev]; n[slotIndex] = true; return n; });
    try {
      const h = await api.getPlayerHistory(playerId, '2023,2024');
      setHistories(prev => { const n = [...prev]; n[slotIndex] = h; return n; });
    } catch {
      setHistories(prev => { const n = [...prev]; n[slotIndex] = null; return n; });
    } finally {
      setHistLoading(prev => { const n = [...prev]; n[slotIndex] = false; return n; });
    }
  }, []);

  useEffect(() => { fetchHistory(0, slots[0]?.id); }, [slots[0]?.id]); // eslint-disable-line
  useEffect(() => { fetchHistory(1, slots[1]?.id); }, [slots[1]?.id]); // eslint-disable-line

  // Demo tour event — auto-fill the two top WRs (or RBs, etc.) for demonstration
  useEffect(() => {
    const handler = () => {
      if (allPlayers.length < 2) return;
      const preferred = ['WR', 'RB', 'QB', 'TE'];
      let p1 = null, p2 = null;
      for (const pos of preferred) {
        const group = allPlayers.filter(p => p.pos === pos);
        if (group.length >= 2) { p1 = group[0]; p2 = group[1]; break; }
      }
      if (p1 && p2) setSlots([p1, p2]);
    };
    window.addEventListener('demo:compareAutoFill', handler);
    return () => window.removeEventListener('demo:compareAutoFill', handler);
  }, [allPlayers]);

  // --- Search helpers ---

  // Returns up to 20 players matching the query string (name, team, or position)
  const getFilteredPlayers = (q) => {
    if (!q) return allPlayers.slice(0, 20);
    return allPlayers.filter(p =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      p.team.toLowerCase().includes(q.toLowerCase()) ||
      p.pos.toLowerCase().includes(q.toLowerCase())
    ).slice(0, 20);
  };

  const selectPlayer = (idx, player) => {
    setSlots(prev => { const n = [...prev]; n[idx] = player; return n; });
    setSearches(prev => { const n = [...prev]; n[idx] = ''; return n; });
    setOpenSlot(null);
  };

  const clearSlot = (idx) => {
    setSlots(prev => { const n = [...prev]; n[idx] = null; return n; });
    setHistories(prev => { const n = [...prev]; n[idx] = null; return n; });
  };

  // --- CSV export ---
  const exportCSV = () => {
    const rows = [
      ['Metric', slots[0]?.name || 'Player 1', slots[1]?.name || 'Player 2'],
      ['Position', slots[0]?.pos || '—', slots[1]?.pos || '—'],
      ['Team', slots[0]?.team || '—', slots[1]?.team || '—'],
      ['Projected Pts (Week)', slots[0]?.proj ?? '—', slots[1]?.proj ?? '—'],
      ['Floor', formatCI(slots[0]?.ciLow), formatCI(slots[1]?.ciLow)],
      ['Ceiling', formatCI(slots[0]?.ciHigh), formatCI(slots[1]?.ciHigh)],
      ['Spread', slots[0]?.ciLow != null ? (slots[0].ciHigh - slots[0].ciLow).toFixed(1) : '—',
                 slots[1]?.ciLow != null ? (slots[1].ciHigh - slots[1].ciLow).toFixed(1) : '—'],
    ];

    // Helper to append a historical average row for any stat key
    const addHistStat = (label, key) => {
      const v0 = histories[0]?.stats?.length
        ? (histories[0].stats.reduce((s, r) => s + (r[key] || 0), 0) / histories[0].stats.length).toFixed(1)
        : '—';
      const v1 = histories[1]?.stats?.length
        ? (histories[1].stats.reduce((s, r) => s + (r[key] || 0), 0) / histories[1].stats.length).toFixed(1)
        : '—';
      rows.push([label, v0, v1]);
    };

    rows.push(['--- Historical Averages (2023-2024) ---', '', '']);
    addHistStat('Avg Fantasy Pts/Game', 'fantasy_points');
    addHistStat('Avg Passing Yds/Game', 'passing_yards');
    addHistStat('Avg Rushing Yds/Game', 'rushing_yards');
    addHistStat('Avg Receiving Yds/Game', 'receiving_yards');
    addHistStat('Avg Receptions/Game', 'receptions');

    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `comparison_${(slots[0]?.name || 'p1').replace(/ /g,'_')}_vs_${(slots[1]?.name || 'p2').replace(/ /g,'_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 2500);
  };

  const activeCount = slots.filter(Boolean).length;

  // Returns the average of a stat key across a player's historical game log
  const histStat = (idx, key) => {
    const h = histories[idx];
    if (!h || !h.stats?.length) return '—';
    const avg = h.stats.reduce((s, r) => s + (r[key] || 0), 0) / h.stats.length;
    return avg.toFixed(1);
  };
  const histTotal = (idx, key) => {
    const h = histories[idx];
    if (!h || !h.stats?.length) return '—';
    return h.stats.reduce((s, r) => s + (r[key] || 0), 0).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans" onClick={() => setOpenSlot(null)}>

      {/* CSV export confirmation toast */}
      <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] transition-all duration-300 ${exportToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        <div className="flex items-center gap-2.5 text-sm font-bold px-5 py-3 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] border bg-[#0c1526] border-green-500/30 text-green-400">
          <Download size={14} /> CSV exported
        </div>
      </div>

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-12 pb-20">

        {/* Page header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
          <div>
            <h1 className="text-3xl sm:text-5xl font-black mb-2 tracking-tight">Player <span className="text-[#4d7cff]">Comparison</span></h1>
            <p className="text-white/40 font-medium">Head-to-head · {season} Week {week} projections + 2023–2024 historical stats</p>
          </div>
          <div className="flex items-center gap-3">
            {activeCount === 2 && (
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 text-xs font-bold text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 rounded-xl transition-all"
              >
                <Download size={13} /> Export CSV
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="text-xs font-bold text-white/40 hover:text-[#4d7cff] transition-colors border border-white/10 px-4 py-2 rounded-xl"
            >
              ← Dashboard
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-white/40">
            <RefreshCw size={18} className="animate-spin" /> Loading players...
          </div>
        ) : (
          <>
            {/* Two player slots */}
            <div data-tour="compare-slots" className="grid gap-6 grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto mb-10">
              {slots.map((player, index) => {
                const hist   = histories[index];
                const hLoad  = histLoading[index];
                const risk   = getRisk(player?.ciLow, player?.ciHigh);
                const accent = index === 0 ? '#4d7cff' : '#a78bfa';

                return (
                  <div key={index} className="flex flex-col gap-4">

                    {/* Search input with autocomplete dropdown */}
                    <div className="relative" onClick={e => e.stopPropagation()}>
                      <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                      <input
                        type="text"
                        placeholder={player ? player.name : 'Search player...'}
                        value={searches[index]}
                        onFocus={() => setOpenSlot(index)}
                        onChange={e => {
                          const n = [...searches]; n[index] = e.target.value; setSearches(n);
                          setOpenSlot(index);
                        }}
                        className="w-full bg-[#0c1526] border border-white/10 rounded-2xl pl-10 pr-10 py-4 text-sm outline-none focus:border-[#4d7cff] transition-all text-white placeholder-white/30"
                      />
                      {player && (
                        <button onClick={() => clearSlot(index)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                          <X size={14} />
                        </button>
                      )}

                      {/* Search dropdown */}
                      {openSlot === index && (
                        <div className="absolute top-full mt-2 w-full bg-[#0c1526] border border-white/[0.08] rounded-2xl overflow-hidden z-50 shadow-2xl max-h-64 overflow-y-auto">
                          {getFilteredPlayers(searches[index]).length === 0 ? (
                            <div className="px-4 py-3 text-white/30 text-sm">No players found</div>
                          ) : (
                            getFilteredPlayers(searches[index]).map(p => (
                              <button key={p.id} onClick={() => selectPlayer(index, p)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/[0.04] last:border-0">
                                <div className="w-8 h-8 rounded-full bg-[#4d7cff]/20 flex-shrink-0 overflow-hidden flex items-center justify-center text-xs font-bold text-[#4d7cff]">
                                  {p.imageUrl ? <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} /> : getInitials(p.name)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold truncate">{p.name}</div>
                                  <div className="text-[10px] text-white/40">{p.team} · {p.pos}</div>
                                </div>
                                <div className="text-[#4d7cff] font-black text-sm">{p.proj}</div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Player card */}
                    <div className={`bg-[#0c1526] border rounded-[24px] sm:rounded-[32px] p-5 sm:p-8 transition-all ${player ? 'border-white/10 shadow-2xl' : 'border-dashed border-white/10 opacity-40'}`}>
                      {player ? (
                        <>
                          {/* Avatar + name header */}
                          <div className="flex items-center gap-5 mb-8">
                            <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 flex items-center justify-center text-xl font-black text-white"
                              style={{ background: `linear-gradient(135deg, ${accent}40, ${accent}20)` }}>
                              {player.imageUrl
                                ? <img src={player.imageUrl} alt={player.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                                : getInitials(player.name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>{player.team}</span>
                                <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${getPosStyle(player.pos)}`}>{player.pos}</span>
                              </div>
                              <h3 className="font-bold text-xl tracking-tight">{player.name}</h3>
                            </div>
                          </div>

                          {/* Weekly projection and CI */}
                          <div className="mb-6">
                            <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-3">Week {week} Projection</div>
                            <div className="text-center py-6 bg-white/[0.02] border border-white/5 rounded-2xl mb-3">
                              <div className="text-4xl sm:text-5xl font-black mb-1" style={{ color: accent }}>{player.proj}</div>
                              <div className="text-[10px] text-white/30 uppercase tracking-widest">projected pts</div>
                            </div>

                            {/* Confidence interval bar */}
                            <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-4">
                              <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-3">Confidence Interval</div>
                              {player.ciLow != null ? (
                                <>
                                  <div className="relative h-2 bg-white/10 rounded-full mb-2">
                                    <div className="absolute h-full rounded-full" style={{ background: `${accent}40`, left: '10%', right: '10%' }}></div>
                                    <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-[#0d0d0d] shadow-lg" style={{ background: accent, left: 'calc(50% - 6px)' }}></div>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-red-400/70 font-bold">{formatCI(player.ciLow)} <span className="text-white/20 font-normal text-[10px]">FLOOR</span></span>
                                    <span className="font-black" style={{ color: accent }}>{player.proj} <span className="text-white/20 font-normal text-[10px]">PROJ</span></span>
                                    <span className="text-green-400/70 font-bold">{formatCI(player.ciHigh)} <span className="text-white/20 font-normal text-[10px]">CEILING</span></span>
                                  </div>
                                  <div className={`mt-3 text-[10px] font-bold uppercase tracking-wider ${risk.color}`}>
                                    <AlertCircle size={10} className="inline mr-1" />{risk.label} · Spread: {(player.ciHigh - player.ciLow).toFixed(1)} pts
                                  </div>
                                </>
                              ) : (
                                <div className="text-white/20 text-xs text-center py-2">No CI data available yet</div>
                              )}
                            </div>
                          </div>

                          {/* Historical stats from 2023-2024 */}
                          <div>
                            <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-3">Historical Stats (2023–2024 avg/game)</div>
                            {hLoad ? (
                              <div className="flex items-center gap-2 text-white/30 text-xs py-4 justify-center">
                                <RefreshCw size={12} className="animate-spin" /> Loading history...
                              </div>
                            ) : hist && hist.stats?.length > 0 ? (
                              <div className="space-y-2">
                                {[
                                  { label: 'Fantasy Pts',   key: 'fantasy_points',   color: accent },
                                  { label: 'Passing Yds',  key: 'passing_yards' },
                                  { label: 'Rushing Yds',  key: 'rushing_yards' },
                                  { label: 'Receiving Yds',key: 'receiving_yards' },
                                  { label: 'Receptions',   key: 'receptions' },
                                ].map(({ label, key, color }) => {
                                  const avg = histStat(index, key);
                                  // Skip stats with no recorded data for this player's position
                                  if (avg === '—' || avg === '0.0') return null;
                                  return (
                                    <div key={key} className="flex justify-between items-center px-4 py-2.5 bg-white/[0.03] rounded-xl border border-white/5">
                                      <span className="text-white/40 text-xs">{label}</span>
                                      <span className="font-bold text-sm" style={color ? { color } : {}}>{avg}</span>
                                    </div>
                                  );
                                })}
                                <div className="flex justify-between items-center px-4 py-2.5 bg-white/[0.03] rounded-xl border border-white/5">
                                  <span className="text-white/40 text-xs">Games Played</span>
                                  <span className="font-bold text-sm">{hist.stats.length}</span>
                                </div>
                                <div className="flex justify-between items-center px-4 py-2.5 bg-white/[0.03] rounded-xl border border-white/5">
                                  <span className="text-white/40 text-xs">Total Fantasy Pts</span>
                                  <span className="font-bold text-sm">{histTotal(index, 'fantasy_points')}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="text-white/20 text-xs text-center py-4 border border-dashed border-white/5 rounded-2xl">
                                No historical data available
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-64">
                          <div className="w-12 h-12 rounded-full border-2 border-dashed border-white/10 flex items-center justify-center mb-4">
                            <span className="text-white/10 text-xl">+</span>
                          </div>
                          <p className="text-[10px] font-black text-white/10 uppercase tracking-widest">Search to Select a Player</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Side-by-side analysis panel */}
            <div className="bg-[#0c1526] border border-white/[0.08] rounded-[24px] sm:rounded-[32px] p-5 sm:p-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#4d7cff]/10 flex items-center justify-center text-[#4d7cff]">
                    <BarChart3 size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Comparison Analysis</h2>
                    <p className="text-white/30 text-xs">2023–2024 season averages</p>
                  </div>
                </div>
                {activeCount === 2 && (
                  <button onClick={exportCSV} className="flex items-center gap-2 text-xs font-bold text-white/40 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 rounded-xl transition-all">
                    <Download size={12} /> Export CSV
                  </button>
                )}
              </div>

              {activeCount < 2 ? (
                <p className="text-white/30 text-sm text-center py-8">
                  Select two players above to see a side-by-side breakdown.
                </p>
              ) : (
                <>
                  {/* Color legend */}
                  <div className="flex gap-6 mb-8">
                    {slots.map((p, i) => p && (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: i === 0 ? '#4d7cff' : '#a78bfa' }}></div>
                        <span className="text-sm font-semibold">{p.name}</span>
                        <span className="text-xs text-white/30">{p.pos} · {p.team}</span>
                      </div>
                    ))}
                  </div>

                  {/* Projected pts / floor / ceiling stat cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {[
                      { label: 'Projected Pts',  v0: slots[0]?.proj,                    v1: slots[1]?.proj,                    fmt: v => v },
                      { label: 'Floor',           v0: slots[0]?.ciLow,                   v1: slots[1]?.ciLow,                   fmt: formatCI },
                      { label: 'Ceiling',         v0: slots[0]?.ciHigh,                  v1: slots[1]?.ciHigh,                  fmt: formatCI },
                    ].map(({ label, v0, v1, fmt }) => {
                      const n0 = parseFloat(v0);
                      const n1 = parseFloat(v1);
                      const hasVals = !isNaN(n0) && !isNaN(n1);
                      return (
                        <div key={label} className="bg-white/[0.03] border border-white/5 rounded-2xl p-4 text-center">
                          <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-3">{label}</div>
                          <div className="flex justify-around items-end gap-2">
                            <div>
                              <div className="text-2xl font-black text-[#4d7cff]">{fmt(v0) ?? '—'}</div>
                              <div className="text-[10px] text-white/30 mt-1 truncate">{slots[0]?.name?.split(' ')[1] || slots[0]?.name}</div>
                            </div>
                            <div className="text-white/20 text-sm font-bold self-center">vs</div>
                            <div>
                              <div className="text-2xl font-black text-[#a78bfa]">{fmt(v1) ?? '—'}</div>
                              <div className="text-[10px] text-white/30 mt-1 truncate">{slots[1]?.name?.split(' ')[1] || slots[1]?.name}</div>
                            </div>
                          </div>
                          {hasVals && (
                            <div className="mt-2 text-[10px] text-white/20">
                              {n0 > n1
                                ? <span className="text-[#4d7cff]">{slots[0]?.name?.split(' ')[1]} leads by {(n0 - n1).toFixed(1)}</span>
                                : n1 > n0
                                ? <span className="text-[#a78bfa]">{slots[1]?.name?.split(' ')[1]} leads by {(n1 - n0).toFixed(1)}</span>
                                : <span>Tied</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Historical bar charts — only rendered if at least one player has history */}
                  {(histories[0] || histories[1]) && (
                    <div className="border-t border-white/5 pt-6">
                      <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold mb-5">Historical Averages Per Game (2023–2024)</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[
                          { key: 'fantasy_points',  label: 'Fantasy Pts' },
                          { key: 'passing_yards',   label: 'Pass Yards' },
                          { key: 'rushing_yards',   label: 'Rush Yards' },
                          { key: 'receiving_yards', label: 'Rec Yards' },
                          { key: 'receptions',      label: 'Receptions' },
                        ].map(({ key, label }) => {
                          // Skip stat rows where neither player has meaningful data
                          const v0 = histories[0]?.stats?.reduce((s, r) => s + (r[key] || 0), 0) / (histories[0]?.stats?.length || 1);
                          const v1 = histories[1]?.stats?.reduce((s, r) => s + (r[key] || 0), 0) / (histories[1]?.stats?.length || 1);
                          if ((!v0 || v0 < 0.1) && (!v1 || v1 < 0.1)) return null;
                          const max = Math.max(v0 || 0, v1 || 0, 1);
                          return (
                            <div key={key}>
                              <div className="text-xs text-white/40 font-bold mb-2">{label}</div>
                              <div className="space-y-2">
                                {[{ val: v0, color: '#4d7cff', name: slots[0]?.name?.split(' ')[1] || slots[0]?.name || 'P1' },
                                  { val: v1, color: '#a78bfa', name: slots[1]?.name?.split(' ')[1] || slots[1]?.name || 'P2' }
                                ].map(({ val, color, name }) => (
                                  <div key={name} className="flex items-center gap-3">
                                    <span className="text-[10px] text-white/30 w-14 text-right flex-shrink-0">{name}</span>
                                    <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{ width: `${Math.max((val / max) * 100, val > 0.1 ? 2 : 0)}%`, background: color, opacity: 0.75 }}
                                      ></div>
                                    </div>
                                    <span className="text-xs font-bold w-10 flex-shrink-0" style={{ color }}>{val > 0.1 ? val.toFixed(1) : '—'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Compare;
